import { useEffect, useRef, useState } from "react";
import { api, type Folder, type ReindexStatus, type ReindexVorschau } from "../api";
import { Select } from "./Select";
import { fehlerMelden, fehlerText, preisFuer, toast, zahlDe } from "./verwaltung";

/**
 * Vorhandene Mails neu einordnen — etwa nachdem Regeln angelegt wurden.
 * Zeigt vorher, was es bewirkt und kostet.
 */
export function ReindexPanel({ folders, onZumModell }: { folders: Folder[]; onZumModell?: () => void }) {
  const [scope, setScope] = useState<"regeln" | "alles">("regeln");
  const [nurNeue, setNurNeue] = useState(true);
  const [folder, setFolder] = useState("");
  const [vorschau, setVorschau] = useState<ReindexVorschau | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const [status, setStatus] = useState<ReindexStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [stoppt, setStoppt] = useState(false);
  const timer = useRef<number | null>(null);
  const anfrage = useRef(0);
  // Eingeordnet wird mit dem günstigeren Modell, falls eins gesetzt ist.
  const [sortierModell, setSortierModell] = useState<string | null>(null);
  useEffect(() => {
    api.setupState().then((s) => setSortierModell(s.llm.fastModel || s.llm.model || null)).catch(() => {});
  }, []);

  const optionen = () => ({ scope, nurNeue, folder: folder || undefined });

  const holeVorschau = async () => {
    const nr = ++anfrage.current;
    setVorschauFehler(null);
    try {
      const v = await api.reindexVorschau(optionen());
      if (nr === anfrage.current) setVorschau(v);
    } catch (e) {
      if (nr === anfrage.current) { setVorschau(null); setVorschauFehler(fehlerText(e)); }
    }
  };

  // Vorschau bei jeder Änderung neu holen — sie kostet nichts. Ältere
  // Antworten, die nach einer neueren eintreffen, werden verworfen.
  useEffect(() => { void holeVorschau(); }, [scope, nurNeue, folder]); // eslint-disable-line react-hooks/exhaustive-deps

  const beobachten = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(async () => {
      const s = await api.reindexStatus().catch(() => null);
      if (!s) return;
      setStatus(s);
      if (!s.laeuft && timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
        setStoppt(false);
        const abgebrochen = s.erledigt < s.gesamt;
        if (abgebrochen) toast.info(`Neu einordnen abgebrochen nach ${s.erledigt.toLocaleString("de-DE")} von ${s.gesamt.toLocaleString("de-DE")} Mails.`);
        else toast.erfolg(`${s.erledigt.toLocaleString("de-DE")} Mails neu eingeordnet.`);
        void holeVorschau();
      }
    }, 1500);
  };

  useEffect(() => {
    // Läuft schon ein Durchgang (etwa nach Neuladen), gleich mitverfolgen.
    api.reindexStatus().then((s) => { setStatus(s); if (s.laeuft) beobachten(); }).catch(() => {});
    return () => { anfrage.current++; if (timer.current) window.clearInterval(timer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const starten = async () => {
    setBusy(true);
    try {
      setStatus(await api.reindexStart(optionen()));
      beobachten();
    } catch (err) {
      fehlerMelden(err, "Start fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const stoppen = async () => {
    setStoppt(true);
    try {
      await api.reindexStop();
    } catch (e) {
      setStoppt(false);
      fehlerMelden(e);
    }
  };

  const laeuft = status?.laeuft ?? false;
  const keinModell = vorschau ? !vorschau.modellVerfuegbar : false;

  // Ohne Zugang darf der teure Umfang nicht ausgewählt bleiben.
  useEffect(() => {
    if (keinModell && scope === "alles") setScope("regeln");
  }, [keinModell, scope]);
  const anteil = status && status.gesamt > 0 ? Math.round((status.erledigt / status.gesamt) * 100) : 0;

  // Nur Eingabe-Tokens; die kurze Antwort je Mail fällt kaum ins Gewicht.
  const preis = preisFuer(sortierModell);
  const kosten = vorschau && preis ? (vorschau.tokensGeschaetzt / 1_000_000) * preis.ein : null;
  const ordnerOptionen = [
    { value: "", label: "Alle außer Papierkorb, Gesendet und Entwürfe" },
    ...[...folders].sort((a, b) => a.path.localeCompare(b.path, "de"))
      .map((f) => ({ value: f.path, label: f.delimiter ? f.path.split(f.delimiter).join(" / ") : f.path, hint: f.total ? f.total.toLocaleString("de-DE") : undefined })),
  ];

  return (
    <>
      <div className="v-kopf">
        <div>
          <h2>Vorhandene Mails neu einordnen</h2>
          <p className="lede">
            Neue Regeln gelten zunächst nur für neu eintreffende Post. Hier lässt du sie über den vorhandenen
            Bestand laufen. Dabei wird <b>nichts verschoben</b> — der Agent ordnet ein und legt Vorschläge an,
            über die du danach entscheidest.
          </p>
        </div>
      </div>

      <div className="field">
        <label id="ri-umfang">Umfang</label>
        <div className="provider-list" role="radiogroup" aria-labelledby="ri-umfang">
          <label className="choice" data-selected={scope === "regeln"}>
            <input type="radio" name="ri-umfang" checked={scope === "regeln"} disabled={laeuft} onChange={() => setScope("regeln")} />
            <span>
              <b>Nur Regeln<span className="dsgvo dsgvo-eu">kostenlos</span></b>
              <span className="hint">
                Wendet deine Regeln und die Spam-Prüfung an. Kein Modellaufruf, nichts verlässt den Server.
              </span>
            </span>
          </label>
          <label className="choice" data-selected={scope === "alles"} data-disabled={keinModell}>
            <input type="radio" name="ri-umfang" checked={scope === "alles"} disabled={laeuft || keinModell} onChange={() => setScope("alles")} />
            <span>
              <b>Regeln und Sprachmodell<span className="dsgvo dsgvo-av-vertrag">kostet</span></b>
              <span className="hint">
                {keinModell ? (
                  <>Nicht möglich, weil kein Modellzugang hinterlegt ist.{" "}
                    {onZumModell && <button type="button" className="v-link" onClick={onZumModell}>Modellzugang einrichten</button>}</>
                ) : "Alles, was keine Regel abfängt, geht zur Einordnung an den Anbieter."}
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="v-raster" style={{ alignItems: "end" }}>
        <div className="field">
          <label>Ordner</label>
          <Select value={folder} disabled={laeuft} onChange={setFolder} options={ordnerOptionen} aria-label="Ordner" />
        </div>
        <div className="field">
          <label className="v-check" style={{ height: 36 }}>
            <input type="checkbox" checked={nurNeue} disabled={laeuft} onChange={(e) => setNurNeue(e.target.checked)} />
            Nur Mails, die noch nicht eingeordnet sind
          </label>
        </div>
      </div>

      {vorschauFehler && !laeuft && (
        <div className="v-meldung" data-art="fehler" role="alert">
          <span>Vorschau nicht verfügbar: {vorschauFehler}</span>
          <button className="btn quiet" onClick={() => void holeVorschau()}>Erneut</button>
        </div>
      )}

      {!vorschau && !vorschauFehler && !laeuft && <div className="v-laden" data-klein="true" role="status">Berechne Vorschau …</div>}

      {vorschau && !laeuft && (
        <div className="v-panel">
          <div className="v-panel-rumpf">
            <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>Was passieren würde</h3>
            <div className="v-kacheln" style={{ marginBottom: 0 }}>
              <div className="v-kachel"><span className="n">{vorschau.gesamt.toLocaleString("de-DE")}</span><span className="k">Mails im Umfang</span></div>
              <div className="v-kachel"><span className="n">{vorschau.perRegel.toLocaleString("de-DE")}</span><span className="k">erledigt eine Regel</span></div>
              <div className="v-kachel" data-art={scope === "alles" && vorschau.perModell > 0 ? "warn" : undefined}>
                <span className="n">{(scope === "alles" ? vorschau.perModell : 0).toLocaleString("de-DE")}</span>
                <span className="k">gehen an das Modell</span>
              </div>
              {vorschau.ausgeschlossen > 0 && (
                <div className="v-kachel"><span className="n">{vorschau.ausgeschlossen.toLocaleString("de-DE")}</span><span className="k">in gesperrten Ordnern (Datenschutz)</span></div>
              )}
            </div>
            {scope === "alles" && vorschau.perModell > 0 && (
              <p className="hint" style={{ marginTop: 12 }}>
                Grob {vorschau.tokensGeschaetzt.toLocaleString("de-DE")} Eingabe-Tokens
                {kosten !== null
                  ? <>, mit {sortierModell} also etwa <b>{kosten < 0.01 ? "unter 0,01" : zahlDe(kosten)} €</b>. Das ist eine Schätzung nach Listenpreis; abgerechnet wird beim Anbieter.</>
                  : <>. {sortierModell ? `Für „${sortierModell}“ ist kein Preis hinterlegt` : "Das Modell ist unbekannt"} — die Kosten stehen in der Abrechnung des Anbieters.</>}
              </p>
            )}
          </div>
        </div>
      )}

      {laeuft && status && (
        <div className="v-fortschritt" data-laeuft="true" role="status" aria-live="polite">
          <div className="v-fortschritt-kopf">
            <strong>{stoppt ? "Wird abgebrochen …" : "Läuft …"}</strong>
            <span className="hint" style={{ margin: 0 }}>{status.erledigt.toLocaleString("de-DE")} von {status.gesamt.toLocaleString("de-DE")}</span>
            <span className="v-prozent">{anteil} %</span>
          </div>
          <div className="bar-track" role="progressbar" aria-label="Fortschritt" aria-valuemin={0} aria-valuemax={status.gesamt} aria-valuenow={status.erledigt}>
            <span className="bar-fill" style={{ width: `${anteil}%` }} />
          </div>
          <p className="v-fortschritt-zeile">
            {status.perRegel.toLocaleString("de-DE")} per Regel, {status.perModell.toLocaleString("de-DE")} per Modell
            {status.uebersprungen > 0 && `, ${status.uebersprungen.toLocaleString("de-DE")} übersprungen`}
            {status.fehler > 0 && `, ${status.fehler} Fehler`}
          </p>
          {status.letzterFehler && <p className="fail-line">{status.letzterFehler}</p>}
        </div>
      )}

      {!laeuft && status?.beendet && (
        <p className="hint" style={{ marginBottom: 14 }}>
          Letzter Durchgang: {status.erledigt.toLocaleString("de-DE")} von {status.gesamt.toLocaleString("de-DE")} eingeordnet
          ({status.perRegel.toLocaleString("de-DE")} per Regel, {status.perModell.toLocaleString("de-DE")} per Modell
          {status.fehler > 0 ? `, ${status.fehler} Fehler` : ""}).
        </p>
      )}

      <div className="v-knoepfe" style={{ marginBottom: 28 }}>
        {laeuft ? (
          <button className="btn danger" onClick={() => void stoppen()} disabled={stoppt}>{stoppt ? "Bricht ab …" : "Abbrechen"}</button>
        ) : (
          <button className="btn primary" disabled={busy || !vorschau || vorschau.gesamt === 0} onClick={() => void starten()}>
            {busy ? "Startet …" : !vorschau ? "Neu einordnen" : vorschau.gesamt === 0 ? "Nichts zu tun" : `${vorschau.gesamt.toLocaleString("de-DE")} Mails neu einordnen`}
          </button>
        )}
      </div>
    </>
  );
}
